import React, { useState } from "react";
import {
  Activity,
  Calendar,
  CreditCard,
  DollarSign,
  FileText,
  Home,
  MessageSquare,
  Settings,
  Target,
  TrendingUp,
  UserPlus,
  Users,
  Info,
  X,
  AlertTriangle
} from "lucide-react";
import {useIsAdmin} from '../hooks/useIsAdmin.ts';
import {useGetProfile} from '../hooks/useProfile.ts';
import {useDashboardSummary, useKitTransactions} from '../hooks/useDashboard.ts';
import {useGroupFeatures} from '../hooks/useGroupFeatures.ts';
import {Link} from 'react-router-dom';
import SmsQuotaWidget from '../components/SmsQuotaWidget';
import { useSubgroups, useSubgroup } from '../hooks/useSubgroups.ts';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const PRICING_ANNOUNCEMENT_ID = 'advanced-plan-price-update-2026-09-v2';

export default function Dashboard() {
  const isAdmin = useIsAdmin();
  const { data: profile } = useGetProfile();
  const currentGroup = profile?.data?.current_group;
  const houseLabel = currentGroup?.house_label || 'House';
  const tenantLabel = currentGroup?.tenant_label || 'Member';
  const [isPricingBannerDismissed, setIsPricingBannerDismissed] = useState(() => {
    return localStorage.getItem(`dismissed_${PRICING_ANNOUNCEMENT_ID}`) === 'true';
  });

  const handleDismissPricingBanner = () => {
    localStorage.setItem(`dismissed_${PRICING_ANNOUNCEMENT_ID}`, 'true');
    setIsPricingBannerDismissed(true);
  };

  // Get group features to filter dashboard sections
  const { selectedFeatures: groupSelectedFeatures, isLoading: isLoadingFeatures } = useGroupFeatures();
  
  // Fetch dashboard data
  const { data: dashboardData, isLoading: isDashboardLoading } = useDashboardSummary();
  
  // Fetch recent transactions for Recent Activity (Admin)
  const { data: transactionsData } = useKitTransactions({ 
    page: 1, 
    per_page: 10 
  });

  // Fetch subgroups data for admin graph and cards
  const { data: subgroupsData } = useSubgroups();
  const subgroups = subgroupsData?.data || [];

  // Member: fetch their specific house detail
  const memberSubgroupId = profile?.data?.member?.subgroup_id;
  const { data: memberHouseData, isLoading: isMemberHouseLoading } = useSubgroup(memberSubgroupId);
  const memberHouse = memberHouseData?.data || null;

  // Milestone toggle for member view
  const [showAllMilestones, setShowAllMilestones] = useState(false);
  
  // Calculate aggregated house collection data for Recharts and Cards
  const houseTotals = React.useMemo(() => {
    return subgroups.reduce((acc, s) => {
      const bk = s.current_month_breakdown || {};
      const actualPaid = Number(s.actual_month_paid ?? bk.actual_month_paid ?? s.this_month_paid ?? 0);
      const thisMonthPaid = Number(s.this_month_paid || 0);
      const thisMonthExpected = Number(s.this_month_expected || 0);

      const toArrears = bk.arrears_applied !== undefined
        ? Number(bk.arrears_applied || 0)
        : Math.max(0, actualPaid - thisMonthPaid);

      const toCurrentMonth = bk.current_month_applied !== undefined
        ? Number(bk.current_month_applied || 0)
        : Math.min(Math.max(0, actualPaid - toArrears), thisMonthExpected);

      const newOverpay = bk.future_applied !== undefined
        ? Number(bk.future_applied || 0)
        : Math.max(0, actualPaid - toCurrentMonth - toArrears);
      
      const totalDebt = Number(s.progress_debt || 0);
      const thisMonthDebt = Math.max(0, thisMonthExpected - thisMonthPaid);
      const prevMonthDebt = Math.max(0, totalDebt - thisMonthDebt);
      
      const cappedThisMonthPaid = Math.min(thisMonthPaid, thisMonthExpected);
      
      return {
        allPaid: acc.allPaid + Number(s.total_contributions || 0),
        monthPaid: acc.monthPaid + cappedThisMonthPaid,
        monthExpected: acc.monthExpected + thisMonthExpected,
        actualPaid: acc.actualPaid + actualPaid,
        totalDebt: acc.totalDebt + totalDebt,
        thisMonthDebt: acc.thisMonthDebt + thisMonthDebt,
        prevMonthDebt: acc.prevMonthDebt + prevMonthDebt,
        totalOverpay: acc.totalOverpay + Number(s.progress_overpay || 0),
        totalToArrears: acc.totalToArrears + toArrears,
        totalToCurrentMonth: acc.totalToCurrentMonth + toCurrentMonth,
        totalNewOverpay: acc.totalNewOverpay + newOverpay,
      };
    }, { allPaid: 0, monthPaid: 0, monthExpected: 0, actualPaid: 0, totalDebt: 0, thisMonthDebt: 0, prevMonthDebt: 0, totalOverpay: 0, totalToArrears: 0, totalToCurrentMonth: 0, totalNewOverpay: 0 });
  }, [subgroups]);

  // Build chart data from REAL monthly_milestones across all houses
  const chartData = React.useMemo(() => {
    // Aggregate milestones by month key across all subgroups
    const monthMap = {};
    const now = new Date();
    const currentMonthKey = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
    
    subgroups.forEach(s => {
      const milestones = s.monthly_milestones || [];
      const expectedPerMonth = Number(s.target_per_month || 0) || Number(milestones[0]?.expected || 0) || 5000;

      milestones.forEach(m => {
        if (!monthMap[m.key]) {
          monthMap[m.key] = { label: m.label, expected: 0, paid: 0, isCurrent: false };
        }
        
        const mExpected = Number(m.expected || 0);
        const mPaid = Number(m.paid || 0);
        const mOverflow = Number(m.overflow || 0);
        
        // The API bundles overflow into the final month's 'paid' amount.
        // We subtract it here so it doesn't show as Purple in the current month,
        // and instead project it forward into future months.
        const actualPaid = Math.max(0, mPaid - mOverflow);

        monthMap[m.key].expected += mExpected;
        monthMap[m.key].paid += actualPaid;
        if (m.is_current) monthMap[m.key].isCurrent = true;
        
        // Handle overflow by projecting it forward into future months
        let remainingOverflow = mOverflow;
        if (remainingOverflow > 0) {
          if (expectedPerMonth <= 0) {
            // Cannot project without an expected rate, dump it back into current month
            monthMap[m.key].paid += remainingOverflow;
          } else {
            let cursorKey = m.key;
            while (remainingOverflow > 0) {
              // Get next month
              let [year, month] = cursorKey.split('-');
              let date = new Date(Number(year), Number(month), 1);
              cursorKey = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0');
              
              let amountForNextMonth = Math.min(remainingOverflow, expectedPerMonth);
              remainingOverflow -= amountForNextMonth;
              
              if (!monthMap[cursorKey]) {
                const monthName = date.toLocaleString('default', { month: 'short', year: '2-digit' });
                monthMap[cursorKey] = { label: monthName, expected: 0, paid: 0, isCurrent: cursorKey === currentMonthKey };
              }
              
              // Allocate target and paid amount to this future month
              monthMap[cursorKey].expected += expectedPerMonth;
              monthMap[cursorKey].paid += amountForNextMonth;
            }
          }
        }
      });
    });

    // Sort by key (YYYY-MM format sorts chronologically) and limit to a 12-month window
    // to prevent infinite graphs (5 months past, current month, 6 months future).
    const minDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    const maxDate = new Date(now.getFullYear(), now.getMonth() + 6, 1);
    const minKey = minDate.getFullYear() + '-' + String(minDate.getMonth() + 1).padStart(2, '0');
    const maxKey = maxDate.getFullYear() + '-' + String(maxDate.getMonth() + 1).padStart(2, '0');

    const sortedKeys = Object.keys(monthMap)
      .filter(key => key >= minKey && key <= maxKey)
      .sort();
    
    return sortedKeys.map(key => {
      const m = monthMap[key];
      const isFuture = key > currentMonthKey;
      
      let paid = 0;
      let unpaid = 0;
      let overpaid = 0;
      
      if (isFuture) {
        // For future months, any money paid is an advance (Purple). 
        // We don't show unpaid (Red) because it's not due yet.
        overpaid = m.paid;
      } else {
        // Past and current months
        paid = Math.min(m.paid, m.expected); // portion that covers the target
        unpaid = Math.max(0, m.expected - m.paid); // shortfall (red)
        overpaid = Math.max(0, m.paid - m.expected); // advance beyond target (only happens if no expectedPerMonth to project)
      }
      
      return {
        name: m.isCurrent ? `${m.label} ★` : m.label,
        Paid: paid,
        Unpaid: unpaid,
        Overpaid: overpaid,
      };
    });
  }, [subgroups]);

  // Helper function to check if a feature is enabled
  const isFeatureEnabled = (featureName) => {
    if (isLoadingFeatures || !groupSelectedFeatures || groupSelectedFeatures.length === 0) {
      return true;
    }
    return groupSelectedFeatures.includes(featureName);
  };

  // Extract data from dashboard summary
  const dashboard = dashboardData || {};
  
  // Calculate metrics from real data
  const totalMembers = dashboard.members?.total || 0;
  const activeMembers = dashboard.members?.active || 0;
  const totalContributions = dashboard.contributions?.total || 0;
  const activeLoans = dashboard.loans?.active || 0;
  const bulkSms = dashboard.bulk_sms;
  const shares = dashboard.shares;
  
  // Use transactions data for recent activity
  const recentTransactions = transactionsData?.data || [];

  const formatTransactionMessage = (transaction) => {
    const memberName = transaction.member?.name;
    const amount = Number(transaction.amount).toLocaleString();
    
    switch (transaction.type) {
      case 'contribution':
        return memberName ? `Payment of KSh ${amount} by ${memberName}` : `Payment of KSh ${amount}`;
      case 'loan':
        return memberName ? `Loan of KSh ${amount} to ${memberName}` : `Loan of KSh ${amount}`;
      case 'repayment':
        return memberName ? `Repayment of KSh ${amount} by ${memberName}` : `Repayment of KSh ${amount}`;
      case 'withdrawal':
        return memberName ? `Withdrawal of KSh ${amount} by ${memberName}` : `Withdrawal of KSh ${amount}`;
      case 'deposit':
        return memberName ? `Deposit of KSh ${amount} by ${memberName}` : `Deposit of KSh ${amount}`;
      case 'transfer':
        return memberName ? `Transfer of KSh ${amount} by ${memberName}` : `Transfer of KSh ${amount}`;
      default:
        return memberName ? `${transaction.type} transaction of KSh ${amount} by ${memberName}` : `${transaction.type} transaction of KSh ${amount}`;
    }
  };

  const getTransactionStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-600';
      case 'pending': return 'bg-yellow-100 text-yellow-600';
      case 'failed': return 'bg-red-100 text-red-600';
      default: return 'bg-green-100 text-green-600';
    }
  };

  if (isDashboardLoading || (isAdmin ? false : isMemberHouseLoading)) {
    return (
      <div className="min-h-screen py-4 px-1 md:px-0">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // MEMBER VIEW — House-focused dashboard
  // ─────────────────────────────────────────────
  if (!isAdmin) {
    // Member not assigned to any house
    if (!memberSubgroupId || !memberHouse) {
      return (
        <div className="min-h-screen py-4 px-1 md:px-0">
          <div className="max-w-7xl mx-auto space-y-6">
            <div className="p-6 max-w-xl mx-auto text-center mt-12 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 p-8 space-y-4">
              <div className="p-4 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 w-16 h-16 mx-auto flex items-center justify-center">
                <Home size={32} />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">No {houseLabel} Assigned</h1>
              <p className="text-gray-500">
                You are currently not assigned to any {houseLabel.toLowerCase()}. Please contact your group administrator to link your account.
              </p>
            </div>
          </div>
        </div>
      );
    }

    const detail = memberHouse;
    const monthPaid = Number(detail.this_month_paid || 0);
    const monthExpected = Number(detail.this_month_expected || 0);

    // Progress bar data
    const paidThru = Number(detail.progress_paid_to_date || 0);
    const expectedThru = Number(detail.progress_expected_to_date || 0);
    const debt = Number(detail.progress_debt || 0);
    const overpay = Number(detail.progress_overpay || 0);
    const totalPaid = Number(detail.target_paid || 0);
    const scale = Math.max(expectedThru, totalPaid, 1);
    const paidPct = (paidThru / scale) * 100;
    const debtPct = (debt / scale) * 100;
    const overpayPct = (overpay / scale) * 100;
    const statusLabel = debt > 0
      ? `Behind by KSh ${debt.toLocaleString()}`
      : overpay > 0
        ? `Overpaid by KSh ${overpay.toLocaleString()}`
        : expectedThru > 0
          ? 'Up to date'
          : 'No monthly target';
    const statusCls = debt > 0
      ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/20 dark:text-rose-400'
      : overpay > 0
        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300'
        : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400';

    // Milestones
    const milestones = detail.monthly_milestones || [];
    const isRelevant = (m) => {
      if (m.is_current) return true;
      if (detail.current_month_label && m.label === detail.current_month_label) return true;
      if (m.status === 'missed' || m.status === 'short' || m.status === 'due') return true;
      if (m.status === 'overpaid' || Number(m.overflow || 0) > 0) return true;
      return false;
    };
    let visibleMilestones = showAllMilestones ? milestones : milestones.filter(isRelevant);
    if (visibleMilestones.length === 0 && milestones.length > 0) {
      const curIdx = milestones.findIndex(m =>
        m.is_current || (detail.current_month_label && m.label === detail.current_month_label)
      );
      visibleMilestones = curIdx >= 0 ? [milestones[curIdx]] : [milestones[0]];
    }
    const someHidden = milestones.length > visibleMilestones.length;

    return (
      <div className="min-h-screen py-4 px-1 md:px-0">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white">
                Dashboard
              </h1>
              <p className="text-gray-500 mt-1">
                Welcome back! Here's your {houseLabel.toLowerCase()} overview for <span className="font-semibold text-gray-700 dark:text-gray-300">{detail.subgroup.name}</span>.
              </p>
            </div>
            <Link
              to="/my-subgroup"
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors flex items-center space-x-2 w-fit"
            >
              <Home size={16} className="text-gray-500" />
              <span>View Full Details</span>
            </Link>
          </div>

          {/* Metric Cards */}
          <div className={`grid grid-cols-1 ${detail.has_target ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
            <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-1">
                  <span className="text-sm text-gray-500 font-medium uppercase tracking-wider block">Co-Occupants</span>
                  <Info size={14} className="text-gray-400 cursor-help" title={`Total number of members in this ${houseLabel.toLowerCase()}.`} />
                </div>
                <span className="text-3xl font-extrabold text-gray-900 dark:text-white mt-1 block">{detail.members.length} {tenantLabel}s</span>
              </div>
              <div className="p-4 rounded-full bg-indigo-50 text-indigo-600">
                <Users size={28} />
              </div>
            </div>

            <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-1">
                  <span className="text-sm text-gray-500 font-medium uppercase tracking-wider block">All-Time Collected</span>
                  <Info size={14} className="text-gray-400 cursor-help" title={`Total lifetime payments for this ${houseLabel.toLowerCase()}.`} />
                </div>
                <span className="text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-1 block">
                  KSh {Number(detail.total_contributions || 0).toLocaleString()}
                </span>
              </div>
              <div className="p-4 rounded-full bg-emerald-50 text-emerald-600">
                <CreditCard size={28} />
              </div>
            </div>

            {detail.has_target && (() => {
              const settled = detail.this_month_status === 'Settled';
              const captionCls = settled ? 'text-emerald-600' : 'text-amber-600';
              return (
                <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-1">
                      <span className="text-sm text-gray-500 font-medium uppercase tracking-wider block">This Month</span>
                      <Info size={14} className="text-gray-400 cursor-help" title="Amount collected towards this month's target." />
                    </div>
                    <span className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1 block">
                      KSh {Math.min(monthPaid, monthExpected).toLocaleString()} <span className="text-base font-medium text-gray-500">/ {monthExpected.toLocaleString()}</span>
                    </span>
                    <span className={`text-sm font-semibold ${captionCls} block mt-1`}>
                      {detail.this_month_status}
                    </span>
                  </div>
                  <div className="p-4 rounded-full bg-amber-50 text-amber-600">
                    <Calendar size={28} />
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Progress Bar */}
          {detail.has_target && (
            <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 space-y-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Progress{detail.current_month_label ? ` · through ${detail.current_month_label}` : ''}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Target period: {detail.target_from_date ? new Date(detail.target_from_date).toLocaleDateString() : '-'} – {detail.target_to_date ? new Date(detail.target_to_date).toLocaleDateString() : '—'} &middot; KSh {monthExpected.toLocaleString()} expected each month
                  </p>
                </div>
                <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${statusCls}`}>{statusLabel}</span>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Paid: <strong className="text-emerald-700">KSh {paidThru.toLocaleString()}</strong></span>
                  <span>Required to date: <strong className="text-gray-900">KSh {expectedThru.toLocaleString()}</strong></span>
                </div>

                <div className="w-full flex bg-gray-100 h-3 rounded-full overflow-hidden">
                  {paidPct > 0 && (
                    <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${paidPct}%` }} title={`Paid: KSh ${paidThru.toLocaleString()}`} />
                  )}
                  {debtPct > 0 && (
                    <div className="h-full bg-rose-500 transition-all duration-500" style={{ width: `${debtPct}%` }} title={`Debt: KSh ${debt.toLocaleString()}`} />
                  )}
                  {overpayPct > 0 && (
                    <div className="h-full bg-purple-500 transition-all duration-500" style={{ width: `${overpayPct}%` }} title={`Overpaid: KSh ${overpay.toLocaleString()}`} />
                  )}
                </div>

                <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Paid KSh {paidThru.toLocaleString()}</span>
                  {debt > 0 && <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose-500" /> Debt KSh {debt.toLocaleString()}</span>}
                  {overpay > 0 && <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-purple-500" /> Pre-pay KSh {overpay.toLocaleString()}</span>}
                </div>

                <p className="text-[11px] text-gray-400 pt-1">
                  Paid in this calendar month: KSh {monthPaid.toLocaleString()} &middot; Total in period: KSh {totalPaid.toLocaleString()} of {Number(detail.target_amount || 0).toLocaleString()}
                </p>
              </div>

              {/* Monthly Milestones */}
              {milestones.length > 0 && (
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">Monthly Milestones</h4>
                      <p className="text-xs text-gray-500">
                        KSh {Number(detail.expected_per_month || 0).toLocaleString()} expected each month &middot; {detail.months_completed}/{detail.total_months} months covered
                      </p>
                    </div>
                    {someHidden && (
                      <button
                        onClick={() => setShowAllMilestones(v => !v)}
                        className="text-xs font-semibold text-blue-600 hover:text-blue-800 underline"
                      >
                        {showAllMilestones ? 'Show relevant only' : `Show all ${milestones.length}`}
                      </button>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5">
                    {visibleMilestones.map((m) => {
                      const cls = m.status === 'paid'
                        ? 'bg-emerald-500 border-emerald-600 text-white'
                        : m.status === 'overpaid'
                        ? 'bg-purple-500 border-purple-600 text-white'
                        : m.status === 'partial'
                        ? 'bg-blue-500 border-blue-600 text-white'
                        : m.status === 'short'
                        ? 'bg-amber-400 border-amber-500 text-amber-950'
                        : (m.status === 'missed' || m.status === 'due')
                        ? 'bg-rose-500 border-rose-600 text-white'
                        : 'bg-gray-100 border-gray-200 text-gray-600';
                      const ring = m.is_current ? 'ring-2 ring-blue-500 ring-offset-1' : '';
                      const title = `${m.label}: KSh ${Number(m.paid).toLocaleString()} / ${Number(m.expected).toLocaleString()}${m.overflow ? ` (+${Number(m.overflow).toLocaleString()} overflow)` : ''} — ${m.status}`;
                      return (
                        <div
                          key={m.key}
                          title={title}
                          className={`px-2 py-1 rounded-md text-[10px] font-semibold border ${cls} ${ring} min-w-[64px] text-center`}
                        >
                          <div>{m.label}</div>
                          {m.overflow > 0 && <div className="text-[9px] opacity-90">+{Number(m.overflow).toLocaleString()}</div>}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-gray-500">
                    <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Paid</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-blue-500" /> Partial</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-400" /> Short</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose-500" /> Missed / Due</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-purple-500" /> Overpaid</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-gray-200" /> Pending</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Payment History (Left - Span 2) */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                  <FileText size={20} className="text-gray-500" />
                  <span>Recent Payment History</span>
                </h3>
                <Link to="/my-subgroup" className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                  View All →
                </Link>
              </div>
              
              {detail.contributions && detail.contributions.length > 0 ? (
                <div className="space-y-3">
                  {detail.contributions.slice(0, 5).map((c, i) => (
                    <div key={c.id || i} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-green-100">
                          <DollarSign className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            KSh {Number(c.amount || 0).toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-500">{c.contribution_date || '-'}</p>
                          {c.member_name && <p className="text-xs text-gray-400">{c.member_name}</p>}
                        </div>
                      </div>
                      <div className="text-right">
                        {c.payment_method && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-800 uppercase">
                            {c.payment_method}
                          </span>
                        )}
                        {c.notes && <p className="text-xs text-gray-400 mt-1 max-w-[200px] truncate">{c.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No recent payment history</p>
                </div>
              )}
            </div>

            {/* Quick Actions Sidebar (Right - Span 1) */}
            <div className="rounded-xl shadow-sm border border-gray-100 p-6 bg-white">
              <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-2">
                <Link
                  to="/my-subgroup"
                  className="p-2 rounded-lg text-center transition-colors bg-blue-50 hover:bg-blue-100"
                >
                  <Home className="w-4 h-4 mx-auto mb-1 text-blue-600" />
                  <span className="text-xs">View {houseLabel}</span>
                </Link>

                {isFeatureEnabled('manual_contribution_management') && (
                  <Link
                    to="/contributions"
                    className="p-2 rounded-lg text-center transition-colors bg-emerald-50 hover:bg-emerald-100"
                  >
                    <DollarSign className="w-4 h-4 mx-auto mb-1 text-emerald-600" />
                    <span className="text-xs">Make Payment</span>
                  </Link>
                )}

                <Link
                  to="/settings"
                  className="p-2 rounded-lg text-center transition-colors bg-gray-50 hover:bg-gray-100"
                >
                  <Settings className="w-4 h-4 mx-auto mb-1 text-gray-600" />
                  <span className="text-xs">Settings</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // ADMIN VIEW — House management dashboard
  // ─────────────────────────────────────────────
  return (
    <div className="min-h-screen py-4 px-1 md:px-0">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white">
                Dashboard
              </h1>
              <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 rounded-full">v2.4</span>
            </div>
            <p className="text-gray-500 mt-1">
              Welcome back! Here's what's happening with your group.
            </p>
          </div>
        </div>

        {/* Advanced Plan Pricing Update Banner (Admin Only) */}
        {isAdmin && !isPricingBannerDismissed && (
          <div className="relative bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-4">
            <button
              onClick={handleDismissPricingBanner}
              className="absolute top-4 right-4 text-amber-500 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-200 transition-colors"
              aria-label="Dismiss"
            >
              <X size={18} />
            </button>
            <div className="flex items-start gap-3 pr-8">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-amber-900 dark:text-amber-100 text-sm mb-1">
                  Advanced Plan pricing update
                </h3>
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  The Advanced Plan price has increased from KSh 500 to KSh 999 due to rising server management fees.
                  This takes effect in your next billing cycle. We've also retired the free plan — new groups now get
                  a 7-day trial period instead.{' '}
                  <a
                    href="https://twachanga.co.ke/pricing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline hover:text-amber-900 dark:hover:text-amber-100"
                  >
                    View pricing details →
                  </a>
                </p>
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-2">
                  We've also released a new version of our mobile app.{' '}
                  <a
                    href="https://play.google.com/store/apps/details?id=co.ke.twachanga"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline hover:text-amber-900 dark:hover:text-amber-100"
                  >
                    Get it on Google Play →
                  </a>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 kpi-grid">
          {/* This Month's Target */}
          {isFeatureEnabled('manual_contribution_management') && (
            <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-between relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-center space-x-1">
                  <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block">This Month's Target (All {houseLabel}s)</span>
                  <Info size={12} className="text-gray-400 cursor-help" title={`The total amount expected from all active ${houseLabel.toLowerCase()}s for the current billing cycle.`} />
                </div>
                <span className="text-2xl font-extrabold text-gray-900 mt-1 block">
                  KSh {houseTotals.monthPaid.toLocaleString()} <span className="text-base font-medium text-gray-500">/ {houseTotals.monthExpected.toLocaleString()}</span>
                </span>
                <span className="text-xs text-gray-500 block mt-1">
                  Remaining to collect: KSh {Math.max(0, houseTotals.monthExpected - houseTotals.monthPaid).toLocaleString()}
                </span>
                <span className="text-[9px] text-gray-400 block">(Excludes advance overpayments)</span>
              </div>
              {/* Progress line */}
              <div className="mt-3 w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-500 rounded-full"
                  style={{ width: `${houseTotals.monthExpected > 0 ? Math.min(100, (houseTotals.monthPaid / houseTotals.monthExpected) * 100) : 0}%` }}
                />
              </div>
            </div>
          )}

          {/* This Month's Collections */}
          {isFeatureEnabled('manual_contribution_management') && (
            <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-between relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-center space-x-1">
                  <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block">This Month's Collections</span>
                </div>
                <span className="text-2xl font-extrabold text-blue-600 mt-1 block">
                  KSh {houseTotals.actualPaid.toLocaleString()}
                </span>
                <span className="text-xs text-gray-500 block mt-1">
                  Target: KSh {houseTotals.monthExpected.toLocaleString()}
                </span>
                {houseTotals.totalToArrears > 0 && (
                  <span className="text-xs font-medium text-amber-500 block">
                    • KSh {houseTotals.totalToArrears.toLocaleString()} filled past arrears
                  </span>
                )}
                {houseTotals.totalToCurrentMonth > 0 && (
                  <span className="text-xs font-medium text-emerald-500 block">
                    • KSh {houseTotals.totalToCurrentMonth.toLocaleString()} went to this month's contribution
                  </span>
                )}
                {houseTotals.totalNewOverpay > 0 && (
                  <span className="text-xs font-medium text-purple-500 block">
                    • KSh {houseTotals.totalNewOverpay.toLocaleString()} went to overpayment (advance)
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Total Arrears */}
          {isFeatureEnabled('manual_contribution_management') && (
            <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-between relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-center space-x-1">
                  <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block">Total Arrears (Past Due)</span>
                  <Info size={12} className="text-gray-400 cursor-help" title="Unpaid balances from previous months combined with any unpaid amount for the current month." />
                </div>
                <span className="text-[9px] text-gray-400 block mb-1">Includes current and previous months</span>
                <span className={`text-2xl font-extrabold mt-1 block ${houseTotals.totalDebt > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {houseTotals.totalDebt > 0 ? `KSh ${houseTotals.totalDebt.toLocaleString()}` : 'No Arrears'}
                </span>
                {houseTotals.totalDebt > 0 && (
                  <div className="mt-2 space-y-0.5">
                    <span className="text-xs font-medium text-rose-500 block">
                      • This month: KSh {houseTotals.thisMonthDebt.toLocaleString()}
                    </span>
                    <span className="text-xs font-medium text-rose-700 block">
                      • Previous months: KSh {houseTotals.prevMonthDebt.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Total Overpayments */}
          {isFeatureEnabled('manual_contribution_management') && (
            <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-between relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-center space-x-1">
                  <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block">Total Overpayments (Advances)</span>
                  <Info size={12} className="text-gray-400 cursor-help" title={`Total advance payments made by ${houseLabel.toLowerCase()}s that exceed their expected target.`} />
                </div>
                <span className={`text-2xl font-extrabold mt-1 block ${houseTotals.totalOverpay > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                  {houseTotals.totalOverpay > 0 ? `KSh ${houseTotals.totalOverpay.toLocaleString()}` : 'None'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Transactions */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Recent Transactions</h3>
              <Link to="/transactions" className="text-sm text-blue-800 hover:text-blue-700 font-medium">
                View All →
              </Link>
            </div>
            <div className="space-y-4">
              {recentTransactions.length > 0 ? (
                recentTransactions.map((transaction, index) => {
                  let isAddition = false;
                  if (transaction.type === 'mpesa') {
                    isAddition = true;
                  } else if (transaction.type === 'group_kit') {
                    isAddition = transaction.raw_payload?.operation === 'add';
                  }
                  
                  return (
                    <div key={index} className="flex items-center p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900">
                            {formatTransactionMessage(transaction)}
                          </p>
                          <span className={`font-bold ${isAddition ? 'text-green-600' : 'text-red-600'}`}>
                            {isAddition ? '+' : '-'}KSh {Number(transaction.amount).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          {transaction.transaction_date ? new Date(transaction.transaction_date).toLocaleDateString() : (transaction.created_at ? new Date(transaction.created_at).toLocaleDateString() : 'Unknown date')}
                        </p>
                        {transaction.member && (
                          <p className="text-xs text-gray-400">Member: {transaction.member.name}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTransactionStatusColor(transaction.status)}`}>
                          {transaction.status}
                        </span>
                        <span className="text-xs text-gray-500 mt-1 capitalize">
                          {transaction.type.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No recent transactions</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Insights */}
          <div className="rounded-2xl shadow-sm border p-6 bg-white">
            <h3 className="text-lg font-semibold mb-4">
              Quick Insights
            </h3>

            <div className="space-y-4">
              {/* SMS Quota Widget */}
              {isFeatureEnabled('bulk_sms') && (
                <SmsQuotaWidget onRechargeClick={() => window.location.href = '/settings?tab=sms-quota'} />
              )}

              {/* Group Health Score */}
              <div className="p-4 rounded-lg border bg-blue-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">
                    Group Health
                  </span>
                  <span className="text-lg font-bold text-blue-600">
                    {totalMembers > 0 ? Math.round((activeMembers / totalMembers) * 100) : 0}%
                  </span>
                </div>

                <div className="w-full rounded-full h-2 bg-blue-200">
                  <div
                    className="h-2 rounded-full transition-all duration-300 bg-blue-600"
                    style={{ width: `${totalMembers > 0 ? (activeMembers / totalMembers) * 100 : 0}%` }}
                  />
                </div>

                <p className="text-xs mt-1">
                  {activeMembers} of {totalMembers} members active
                </p>
              </div>

              {/* Quick Actions */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">
                  Quick Actions
                </h4>

                <div className="grid grid-cols-2 gap-2">
                  {isFeatureEnabled('manual_contribution_management') && (
                    <Link
                      to="/contributions"
                      className="p-2 rounded-lg text-center transition-colors
                                bg-blue-50 hover:bg-blue-100"
                    >
                      <DollarSign className="w-4 h-4 mx-auto mb-1 text-blue-600" />
                      <span className="text-xs">Record Payment</span>
                    </Link>
                  )}

                  {isFeatureEnabled('members_management') && (
                    <Link
                      to="/members"
                      className="p-2 rounded-lg text-center transition-colors
                                bg-green-50 hover:bg-green-100"
                    >
                      <UserPlus className="w-4 h-4 mx-auto mb-1 text-green-800" />
                      <span className="text-xs">Add Member</span>
                    </Link>
                  )}

                  {isFeatureEnabled('bulk_sms') && (
                    <Link
                      to="/bulk-sms"
                      className="p-2 rounded-lg text-center transition-colors
                                bg-purple-50 hover:bg-purple-100"
                    >
                      <MessageSquare className="w-4 h-4 mx-auto mb-1 text-orange-800" />
                      <span className="text-xs">Send SMS</span>
                    </Link>
                  )}

                  <Link
                    to="/houses"
                    className="p-2 rounded-lg text-center transition-colors
                              bg-indigo-50 hover:bg-indigo-100"
                  >
                    <Home className="w-4 h-4 mx-auto mb-1 text-indigo-600" />
                    <span className="text-xs">Manage {houseLabel}s</span>
                  </Link>
                </div>
              </div>

              {/* Fun Fact */}
              {isFeatureEnabled('manual_contribution_management') && (
                <div className="p-3 rounded-lg border bg-yellow-50">
                  <div className="flex items-center">
                    <Target className="w-4 h-4 mr-2 text-yellow-800" />
                    <span className="text-xs font-medium">
                      Did you know?
                    </span>
                  </div>

                  <p className="text-xs mt-1">
                    Your group has collected KSh{" "}
                    {Number(totalContributions).toLocaleString()} in total contributions!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Status Chart */}
        {isFeatureEnabled('manual_contribution_management') && chartData.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Status</h3>
            <div className="h-80 w-full mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 25 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tickMargin={10} tick={{fill: '#6b7280', fontSize: 12}} />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{fill: '#6b7280', fontSize: 12}}
                    tickFormatter={(value) => `KSh ${value >= 1000 ? (value/1000).toFixed(0) + 'k' : value}`}
                  />
                  <Tooltip 
                    cursor={{fill: '#f9fafb'}}
                    formatter={(value, name) => [`KSh ${Number(value).toLocaleString()}`, name]}
                    contentStyle={{ borderRadius: '12px', border: '1px solid #f3f4f6', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)' }}
                  />
                  <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px', fontSize: '12px' }} />
                  <Bar dataKey="Paid" stackId="a" fill="#10b981" maxBarSize={80} />
                  <Bar dataKey="Unpaid" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={80} />
                  <Bar dataKey="Overpaid" stackId="a" fill="#a855f7" radius={[4, 4, 0, 0]} maxBarSize={80} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-6 text-center space-y-1">
              <p className="text-sm text-gray-600">Monthly payment status across all {houseLabel.toLowerCase()}s · ★ = current month</p>
              {chartData.some(d => d.Overpaid > 0) && (
                <p className="text-xs text-purple-600 font-medium">Advance overpayments are projected forward in purple to the months they cover</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
